import { make } from 'vuex-pathify'
import axios from 'axios'
import uuid from '@/helpers/uuid'
import sphericalDistance from '@/helpers/sphericalDistance'
import { startOfYesterday } from 'date-fns'
import { athleticsKeys, artsKeys, educationalKeys } from './keys.js'

const state = {
  // Search results
  allSchools: [],
  suggestions: [],
  searchId: '',
  offerings: [],
  excludedSchoolId: '7ab6f0b0-3304-11ef-92d6-ed16a99dd422',
  // Search filters
  keywords: '',
  selectedEducationLevels: [],
  selectedEducationalAttributes: [],
  selectedArtsOfferings: [],
  selectedAthleticsOfferings: [],
  maxDistance: 26,

  // Search options
  enableFuzzySearch: true,
}

export default {
  namespaced: true,

  state,

  mutations: make.mutations(state),

  actions: {
    ...make.actions(state),

    async getAllSchools({ commit, state }) {
      // Make a graphql post request for schools
      const { data } = await axios.post(
        process.env.VUE_APP_GRAPHQL_ENDPOINT,
        {
          query: `
        query schools {
          offerings(order_by: { title: asc }) {
            id
            title
            acronym
            shortCode
            description
            categories {
              category {
                id
                title
              }
            }
          }
          organizations(order_by: {title: asc}, where: {parentOrganizationId: {_is_null: false}}) {
            # Identifiers
            id
            title
            abbreviation
            educationLevels
            gradeLevels
            schoolType
            feederOrganizations
            instructionTypes
            status

            # Design & Branding
            exteriorPhotoUrl
            logoImageUrl
            mascotImageUrl
            primaryColor
            secondaryColor
            tagline
            elevatorPitch
            mission
            vision

            # Contact Details
            website
            hours
            phone
            fax
            email
            address
            facebook
            instagram
            twitter
            yearFounded
            administrators

            # Enrollment
            enrollment
            openSeats

            # Location
            boundaryMapCoordinates
            location

            # Attributes
            uniqueOfferings
            awardsHonors
            offerings {
              offering {
                id
                shortCode
              }
            }
          }
        }
        `,
          variables: {},
        }
      )

      // Update state
      if (data && data.data && data.data.organizations) {
        const filteredOrganizations = data.data.organizations.filter(
          org => org.id !== state.excludedSchoolId
        )
        commit('SET_ALL_SCHOOLS', filteredOrganizations)
      }
      if (data && data.data && data.data.offerings) {
        commit('SET_OFFERINGS', data.data.offerings)
      }
    },

    async querySchools({ commit, dispatch, state, getters }) {
      // Create new search id
      commit('SET_SEARCH_ID', uuid())
      if (state.keywords) dispatch('trackQuery')
      return getters.schools
    },

    async trackQuery({ state }) {
      // Google Tag Manager
      if (typeof window.dataLayer !== 'undefined') {
        window.dataLayer.push({
          event: 'school-search',
          searchId: state.searchId,
          queryTerms: state.keywords,
          resultCount: state.schools.length,
        })
      }
    },

    trackClick({ state }, school) {
      // Google Tag Manager
      if (typeof window.dataLayer !== 'undefined') {
        window.dataLayer.push({
          event: 'school-click',
          searchId: state.searchId,
          schoolId: school.abbreviation.toLowerCase(),
          rank:
            state.schools.findIndex(
              s => s.abbreviation === school.abbreviation
            ) + 1,
        })
      }
    },

    clearFilters({ commit, dispatch }) {
      commit('SET_SELECTED_EDUCATION_LEVELS', [])
      commit('SET_SELECTED_EDUCATION_LEVELS', [])
      commit('SET_SELECTED_EDUCATIONAL_ATTRIBUTES', [])
      commit('SET_SELECTED_ARTS_OFFERINGS', [])
      commit('SET_SELECTED_ATHLETICS_OFFERINGS', [])
      commit('SET_MAX_DISTANCE', 26)
      dispatch('location/reset', null, { root: true })
    },
  },

  getters: {
    facets: (state, getters, rootState, rootGetters) => {
      // Filter out excluded school by ID
      let filteredSchools = state.allSchools.filter(s => s.id !== state.excludedSchoolId)
      // Create a function that maps an offering to an object with its key and a count of how many schools offer it

      const mapOffering = offering => ({
        value: offering.shortCode,
        count: getters.schools.filter(s =>
          s.offerings.some(o => o.offering.shortCode === offering.shortCode)
        ).length,
      })

      return {
        educationalAttributes: state.offerings
          .filter(offering => {
            return educationalKeys.some(key => offering.shortCode === key)
          })
          .map(mapOffering),
        athleticsOfferings: state.offerings
          .filter(offering => {
            return athleticsKeys.some(key => offering.shortCode === key)
          })
          .map(mapOffering),
        artsOfferings: state.offerings
          .filter(offering => {
            return artsKeys.some(key => offering.shortCode === key)
          })
          .map(mapOffering),
        educationLevels: [
          { value: 'Preschool', count: filteredSchools.filter(s => s.educationLevels.includes('Preschool')).length },
          { value: 'Kindergarten', count: filteredSchools.filter(s => s.educationLevels.includes('Kindergarten')).length },
          { value: 'Elementary', count: filteredSchools.filter(s => s.educationLevels.includes('Elementary')).length },
          { value: 'Middle', count: filteredSchools.filter(s => s.educationLevels.includes('Middle')).length },
          { value: 'High', count: filteredSchools.filter(s => s.educationLevels.includes('High')).length },
        ],
      }
    },
    schools: (state, getters, rootState, rootGetters) => {
      return (
        state.allSchools
          // Filter out excluded school by ID
          .filter(school => school.id !== state.excludedSchoolId)
          // filter by keywords
          .filter(school => {
            if (!state.keywords) return true
            // match against title, educationLevels, and abbreviation
            return (
              school.title
                .toLowerCase()
                .includes(state.keywords.toLowerCase()) ||
              school.educationLevels
                .join(', ')
                .toLowerCase()
                .includes(state.keywords.toLowerCase()) ||
              school.abbreviation
                .toLowerCase()
                .includes(state.keywords.toLowerCase())
            )
          })
          // filter by education levels
          .filter(school => {
            if (state.selectedEducationLevels.length === 0) return true
            // Determine if the school has any of the selected education levels
            return state.selectedEducationLevels.some(level => school.educationLevels.includes(level))
          })
          // filter by educational attributes
          .filter(school => {
            if (state.selectedEducationalAttributes.length === 0) return true
            // Determine if the school has any offerings with short codes that match any of the selected educational attributes
            return state.selectedEducationalAttributes.some(attribute => {
              return school.offerings.filter(offering => offering.offering.shortCode === attribute).length > 0
            })
          })
          // filter by arts offerings
          .filter(school => {
            if (state.selectedArtsOfferings.length === 0) return true
            // Determine if the school has any offerings with short codes that match any of the selected arts offerings
            return state.selectedArtsOfferings.some(offering => {
              return school.offerings.filter(o => o.offering.shortCode === offering).length > 0
            })
          })
          // filter by athletics offerings
          .filter(school => {
            if (state.selectedAthleticsOfferings.length === 0) return true
            // Determine if the school has any offerings with short codes that match any of the selected athletics offerings
            return state.selectedAthleticsOfferings.some(offering => {
              return school.offerings.filter(o => o.offering.shortCode === offering).length > 0
            })
          })
          // filter by distance
          .filter(school => {
            if (state.maxDistance === 26) return true
            console.log
            if (!rootGetters['location/userLocation']) return true

            return (
              sphericalDistance(
                school.location.lat,
                school.location.lon,
                rootGetters['location/userLocation'].lat,
                rootGetters['location/userLocation'].lon
              ) <= state.maxDistance
            )
          })
      )
    },
    schoolsWithMetadata: (state, getters, rootState, rootGetters) => {
      return getters.schools.map(school => {
        const userLocation = rootGetters['location/userLocation']
        const schoolLocation = school.location
          ? school.location.coordinates
          : null
        const distance =
          userLocation && schoolLocation
            ? sphericalDistance(
                userLocation.lat,
                userLocation.lon,
                schoolLocation[1],
                schoolLocation[0]
              )
            : -1
        return {
          ...school,
          distance,
        }
      })
    },

    totalFiltersApplied: (state, getters, rootState, rootGetters) => {
      return (
        (!rootGetters['location/userLocation'] ? 0 : 1) +
        (!rootState['location/referenceAddress'] ? 0 : 1) +
        (state.maxDistance !== 26 ? 1 : 0) +
        state.selectedEducationLevels.length +
        state.selectedEducationalAttributes.length +
        state.selectedArtsOfferings.length +
        state.selectedAthleticsOfferings.length
      )
    },

    searchPayload: (state, getters, rootState, rootGetters) => {
      const userLocation = rootGetters['location/userLocation']

      let query = '*'

      if (state.enableFuzzySearch) {
        // Search both by exact phrase and by fuzzy search of school name
        query = state.keywords.trim()
          ? `"${state.keywords.trim()}" OR name:${state.keywords
              .trim()
              .split(' ')
              .join('~ OR name:')}~`
          : '*'
      } else {
        query = state.keywords.trim()
      }

      let filters = []

      if (state.selectedEducationLevels.length > 0) {
        filters.push(
          `educationLevels/any(t: search.in(t, '${state.selectedEducationLevels.join(
            ', '
          )}'))`
        )
        // filters.push(`educationLevels/any(t: t eq '${state.selectedEducationLevels.join("') and educationLevels/any(t: t eq '")}') `)
      }

      if (state.selectedEducationalAttributes.length > 0) {
        filters.push(
          `educationalAttributes/any(t: t eq '${state.selectedEducationalAttributes.join(
            "') and educationalAttributes/any(t: t eq '"
          )}') `
        )
      }

      if (state.selectedArtsOfferings.length > 0) {
        filters.push(
          `artsOfferings/any(t: t eq '${state.selectedArtsOfferings.join(
            "') and artsOfferings/any(t: t eq '"
          )}') `
        )
      }

      if (state.selectedAthleticsOfferings.length > 0) {
        filters.push(
          `athleticsOfferings/any(t: t eq '${state.selectedAthleticsOfferings.join(
            "') and athleticsOfferings/any(t: t eq '"
          )}')  `
        )
      }

      if (userLocation && state.maxDistance && state.maxDistance <= 25) {
        const maxDistanceInKilometers = state.maxDistance * 1.609344
        filters.push(
          `geo.distance(location, geography'POINT(${userLocation.lon} ${
            userLocation.lat
          })') le ${maxDistanceInKilometers}`
        )
      }

      let payload = {
        count: true,
        queryType: state.enableFuzzySearch ? 'full' : 'simple',
        search: query,
        filter: filters.join(' and '),
        facets: [
          'educationLevels,count:1000',
          'educationalAttributes,count:1000',
          'athleticsOfferings,count:1000',
          'artsOfferings,count:1000',
        ],
      }

      if (userLocation) {
        payload.orderby = `geo.distance(location, geography'POINT(${
          userLocation.lon
        } ${userLocation.lat})') asc`
      } else {
        payload.orderby = `name asc`
      }

      return payload
    },
    educationalAttributes: state => {
      return state.offerings
    },
  },
}
